class DSCartDrawer extends HTMLElement {
  constructor() {
    super();

    document.addEventListener('keyup', (evt) => {
      if (evt.code === 'Escape' && this.classList.contains('active')) this.close();
    });
    const ATC_BTN_SELECTOR = 'button[name="add"], .atc-small';
    const findAtcButton = (initiator) => {
      if (!(initiator instanceof Element)) return null;
      return initiator.matches(ATC_BTN_SELECTOR)
        ? initiator
        : initiator.querySelector(ATC_BTN_SELECTOR);
    };
    // Submit-Buttons ausserhalb des Formulars (z. B. Sticky-Leiste auf der PDP) hängen
    // per form-Attribut am Produktformular und tragen kein name="add".
    const findProductForm = (initiator) => {
      if (!(initiator instanceof Element)) return null;
      return initiator.matches('form') ? initiator : initiator.querySelector('form');
    };
    const findClickedAtcButton = (target) => {
      const submitBtn = target?.closest?.('button[type="submit"]');
      if (submitBtn?.form?.closest('ajax-cart-product-form')) return submitBtn;
      return target?.closest?.(ATC_BTN_SELECTOR);
    };

    this._userAtcClickInfo = null;
    document.addEventListener('click', (event) => {
      const btn = findClickedAtcButton(event.target);
      if (!btn) return;
      const quickViewModal = btn.closest('quick-view-modal');
      const fromCard = !!(
        btn.closest('.product-card') ||
        quickViewModal ||
        btn.closest('[data-view-mode="quick-view"]') ||
        btn.closest('.rezept-details__ingredient') ||
        btn.matches('.rezept-details__atc-btn')
      );
      this._userAtcClickInfo = {
        fromCard,
        quickViewModal,
        timestamp: Date.now(),
      };
    }, true);

    document.addEventListener("liquid-ajax-cart:request-start", (event) => {
      const { requestState } = event.detail;
      if (requestState.requestType === "add") {
        const btn = findAtcButton(requestState.info?.initiator);
        if (btn) btn.classList.add('btn--loading');
      }
    });

    document.addEventListener("liquid-ajax-cart:request-end", (event) => {
      const { requestState, cart } = event.detail;

      if (requestState.requestType === "add") {
        const btn = findAtcButton(requestState.info?.initiator);
        if (btn) {
          btn.classList.remove('btn--loading');
          btn.removeAttribute('aria-busy');
        }
        // theme.js (custom-button) setzt aria-busy auf jeden Submit-Button des Formulars.
        Array.from(findProductForm(requestState.info?.initiator)?.elements ?? [])
          .filter((el) => el.type === 'submit')
          .forEach((el) => el.removeAttribute('aria-busy'));
      }

      if (requestState.requestType == "add" && requestState.responseData?.ok) {
        const click = this._userAtcClickInfo;
        const isUserInitiated = click && (Date.now() - click.timestamp) < 5000;

        if (isUserInitiated) {
          this._userAtcClickInfo = null;
          const fromCard = click.fromCard;
          const quickViewModal = click.quickViewModal;
          const useToast = !!window.CartToastUtils?.shouldUseToast?.({ isFromCard: fromCard });

          if (useToast) {
            const body = requestState.responseData.body;
            const addedItem = body?.items?.[0] ?? body;
            const hasQuickView = !!quickViewModal;
            window.CartToastUtils?.show({
              productTitle: addedItem?.product_title ?? addedItem?.title,
              bumpHeaderIcon: !hasQuickView,
            });
            if (hasQuickView) {
              const afterHideEvent = quickViewModal.events?.handleAfterHide || 'drawer:handleAfterHide';
              quickViewModal.addEventListener(
                afterHideEvent,
                () => window.CartToastUtils?.bumpHeaderIcon(),
                { once: true }
              );
            }
          } else {
            this.open();
          }

          if (quickViewModal && typeof quickViewModal.hide === 'function') {
            quickViewModal.hide(true);
          }
        }
      }

      // Update visibility based on cart state
      this.updateCartEmptyState(cart);
      this.updateCartCountBubble(cart);
    });
    this.addEventListener('click', (event) => {
      if (event.target.matches('#CartDrawer-Overlay')) {
        this.close();
      }
    });
    this.setHeaderCartIconAccessibility();
  }

  updateCartEmptyState(cart) {
    const isEmpty = !cart || cart.item_count === 0;
    const emptyWrapper = this.querySelector('.ds-drawer__inner-empty');
    const header = this.querySelector('.ds-drawer__header');
    const cartContent = this.querySelector('.ds-cart-content');
    const cartDrawerItems = this.querySelector('cart-drawer-items');

    if (isEmpty) {
      this.classList.add('is-empty');
      // Show empty state elements
      if (emptyWrapper) emptyWrapper.style.display = 'block';
      if (header) header.style.display = 'flex';
      // Hide cart content wrapper
      if (cartContent) cartContent.style.display = 'none';
      if (cartDrawerItems) cartDrawerItems.classList.add('is-empty');
    } else {
      this.classList.remove('is-empty');
      // Hide empty state
      if (emptyWrapper) emptyWrapper.style.display = 'none';
      // Show cart content wrapper
      if (header) header.style.display = 'flex';
      if (cartContent) cartContent.style.display = 'flex';
      if (cartDrawerItems) cartDrawerItems.classList.remove('is-empty');
    }
  }

  updateCartCountBubble(cart) {
    const bubble = document.querySelector('#ds-cart-icon-bubble cart-count');
    if (!bubble) return;
    const count = cart ? cart.item_count : 0;
    if (count > 0) {
      bubble.removeAttribute('hidden');
    } else {
      bubble.setAttribute('hidden', '');
    }
    const label = count === 1 ? '1 Artikel' : `${count} Artikel`;
    bubble.setAttribute('aria-label', label);
  }

  connectedCallback() {
    // Set initial cart state on mount
    if (window.liquidAjaxCart && window.liquidAjaxCart.cart) {
      this.updateCartEmptyState(window.liquidAjaxCart.cart);
      this.updateCartCountBubble(window.liquidAjaxCart.cart);
    }

    // Open cart drawer if open_cart=1 is in the URL
    const params = new URLSearchParams(window.location.search);
    if (params.get('open_cart') === '1') {
      // Small delay to ensure the drawer is fully rendered
      setTimeout(() => this.open(), 300);
      // Clean up the URL parameter
      params.delete('open_cart');
      const newUrl = params.toString()
        ? `${window.location.pathname}?${params.toString()}`
        : window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
  }

  disconnectedCallback() {
    document.body.classList.remove('overflow-hidden', 'modal-show', 'modal-showing');
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#ds-cart-icon-bubble');

    if (!cartLink) {
      setTimeout(() => this.setHeaderCartIconAccessibility(), 500);
      return;
    }

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    window.CartToastUtils?.hide();
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        // trapFocus(...);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden', 'modal-show', 'modal-showing');
  }

  close() {
    this.classList.remove('active');
    // removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden', 'modal-show', 'modal-showing');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {

      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'ds-cart-icon-bubble',
      },
    ];
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}




class CartDrawerItems extends HTMLElement {
  constructor() {
    super();
  }
}

customElements.define('ds-cart-drawer', DSCartDrawer);
customElements.define('cart-drawer-items', CartDrawerItems);
